import { Eye, FlaskRound, CalendarClock, Wand2, Building2, Crown, UploadCloud, Plus } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { Heading, NextStep } from "@/components/landing/HomeSections";
import { scrollToWaitlist } from "@/components/landing/CtaBits";

export const PIPELINE = [
  [Eye, "AI Vision", "Upload a product photo, get a complete, Etsy-ready listing in seconds: title, 13 tags and description built around the words buyers search for, plus confidence scoring and condition detection for vintage."],
  [UploadCloud, "Bulk upload", "Upload hundreds of products at once — photos in, ready-to-review Etsy drafts out. No more 20–30 minutes per listing."],
  [FlaskRound, "Staged rollouts & drop guard", "Test on a few listings first, with undo suggested if views drop."],
  [CalendarClock, "Seasonal alerts", "We flag the right listings ~90 days before Valentine's Day, Mother's Day, Halloween, Black Friday and Christmas — with keyword suggestions ready to approve."],
  [Wand2, "Bulk attribute & title fixer", "Fill missing attributes and apply Etsy's title guidelines across many listings."],
  [Building2, "Multi-shop & agency workspace", "Several shops, one calm workspace."],
];
// One-line versions for the compact homepage grid.
const SHORT = [
  "A product photo in; an Etsy-ready title, 13 tags and description out.",
  "Hundreds of photos in, ready-to-review Etsy drafts out.",
  "Test on a few listings first, with undo suggested if views drop.",
  "The right listings flagged ~90 days before the big holidays.",
  "Fill missing attributes and apply Etsy's title guidance at scale.",
  "Several shops, one calm workspace.",
];

export const SoonBadge = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E9C98B]/35 bg-[#E9C98B]/[0.06] px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] text-[#F3DDAE]">
    <span className="h-1 w-1 rounded-full bg-[#E9C98B]" />Coming soon
  </span>
);

const PipelineCard = ({ item, i, compact }) => {
  const [Icon, title, text] = item;
  return (
    <Reveal delay={(i % 3) * 100} data-testid={`pipeline-card-${i}`}
      className={`soon-card glass lift relative overflow-hidden border ${compact ? "rounded-2xl p-5" : "rounded-3xl p-6"}`} style={{ "--d": `${i * 0.8}s` }}>
      <div className="flex items-start justify-between gap-3">
        <span className="icon-float relative grid h-11 w-11 place-items-center rounded-2xl border border-[#E9C98B]/25 bg-[#0E1020] shadow-[0_0_28px_-8px_#E9C98B]" style={{ "--d": `${i * 0.6}s` }}>
          <Icon className="h-5 w-5 text-[#E9C98B]" />
        </span>
        <SoonBadge />
      </div>
      <h3 className={`${compact ? "mt-5 text-2xl" : "mt-7 text-[1.7rem] leading-[1.1]"}`}>{title}</h3>
      <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{text}</p>
    </Reveal>
  );
};

export const FoundingPriceNote = ({ testid = "founding-price-note" }) => (
  <Reveal data-testid={testid} className="glass hairline-gold mx-auto flex max-w-3xl flex-col items-center gap-3 rounded-3xl border px-6 py-7 text-center sm:flex-row sm:text-left">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#E9C98B]/40 bg-[#E9C98B]/[0.06]">
      <Crown className="h-5 w-5 text-[#E9C98B]" />
    </span>
    <div>
      <p className="font-display text-2xl sm:text-[1.7rem] leading-tight">Founding members lock a <span className="italic text-gold-grad">lifetime founding price.</span></p>
      <p className="mt-1 text-sm text-muted-foreground">Free during the beta. No card needed.</p>
    </div>
  </Reveal>
);

export const Pipeline = ({ compact = false, lead = null, tail = null }) => (
  <div data-testid={compact ? "pipeline-compact" : "pipeline"} className="space-y-4 sm:space-y-5">
    {lead}
    <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3 [@media(min-width:1024px)_and_(orientation:landscape)]:grid-cols-3">
      {PIPELINE.map((item, i) => <PipelineCard key={item[1]} item={item} i={i} compact={compact} />)}
    </div>
    {tail}
  </div>
);

const VisionTile = () => (
  <Reveal className="hairline-gold relative h-[170px] overflow-hidden rounded-2xl lg:h-auto lg:min-h-[260px]">
    <img src="/images/vision.webp" alt="AI Vision, a coming AetherTags feature, scanning a vintage pocket watch and brooch to draft an Etsy listing title and tags" loading="lazy" decoding="async" width="1264" height="848" className="absolute inset-0 h-full w-full object-cover object-[50%_40%]" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#07080F]/95 via-[#07080F]/60 to-transparent lg:bg-gradient-to-t" />
    <div className="absolute inset-y-0 left-0 flex max-w-lg flex-col justify-center p-6 lg:justify-end">
      <SoonBadge />
      <p className="mt-3 text-[15px] text-foreground/90 leading-relaxed">Six features in the pipeline, including an AI Etsy listing generator. No dates until they're ready.</p>
    </div>
  </Reveal>
);

export const ShapedTile = () => (
  <Reveal data-testid="pipeline-shaped-tile" className="glass hairline-gold relative flex flex-col gap-4 overflow-hidden rounded-2xl border p-5 sm:flex-row sm:items-center sm:gap-5 sm:px-7">
    <div aria-hidden="true" className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#E9C98B]/15 blur-3xl" />
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-[#E9C98B]/30 bg-[#0E1020]"><Plus className="h-5 w-5 text-[#E9C98B]" /></span>
    <p className="font-display text-2xl leading-tight">And whatever <span className="italic text-gold-grad">you ask for.</span></p>
    <p className="text-[15px] text-muted-foreground leading-relaxed sm:ml-auto">Founding members shape what we build next.</p>
  </Reveal>
);

export const Showcase = () => (
  <section data-testid="whats-coming" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
    <Heading eyebrow="What's coming">Built for today. <span className="italic text-aurora">More Etsy SEO tools coming.</span></Heading>
    <div className="mt-8 grid gap-3 lg:grid-cols-[1fr_2fr]">
      <VisionTile />
      <div className="min-w-0">
        <p className="mb-2 text-xs text-muted-foreground sm:hidden">Swipe for all six →</p>
        <Reveal className="hairline flex snap-x snap-mandatory gap-px overflow-x-auto overscroll-x-contain rounded-2xl border bg-white/[0.07] [scrollbar-width:none] sm:grid sm:grid-cols-2">
          {PIPELINE.map(([Icon, title], i) => (
            <div key={title} data-testid={`pipeline-card-${i}`} className="min-w-[78%] snap-start bg-[#0A0B14] px-5 py-4 sm:min-w-0">
              <h3 className="flex items-center gap-2.5 font-display text-xl leading-tight"><Icon className="h-4 w-4 shrink-0 text-[#E9C98B]" />{title}</h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{SHORT[i]}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </div>
    <NextStep onClick={scrollToWaitlist} testid="coming-next-step">Founding members shape what we build next: join the founding list</NextStep>
  </section>
);
